/**
 * What only a garden's owner may change (B37; owner, 2026-10-20: "an invitee
 * should not be able to change garden location or any profile data that would
 * impact the inviter"): Today's garden picture, the plant, bed and season
 * Public switches and a photo's Public toggle, deleting a bed and its sun,
 * shade and shelter. The server refuses a member those (403 OWNER_ONLY,
 * bloom-backend middleware/ownerOnly); the app locks them, with the same
 * words, so a member is not offered what will be refused.
 *
 * One member signal (B23 and B37): whether the person is tending someone
 * else's garden is the server's tendedOwnerOf (bloom-backend
 * middleware/gardenContext), which the app reads as GET /billing/status
 * tendingFor through useTendingFor (features/billing/lib/useBilling). The
 * plan's screens and these locks read that same answer. Nothing is locked
 * until the status is in, and the server still refuses then.
 *
 * Who uses it: TodayPictureSetting, PublicPageSwitch, PlantCard (the photo's
 * Public toggle), EditLocationDialog (Delete; sun, shade and shelter through
 * BedFactsFields), SeasonReviewPage (the season's Public switch),
 * ProfileDetailsForm (the postcode is not the garden's), useShareHandles.
 * Words (en/es): garden.ownerOnly.
 *
 * @module useOwnerOnly
 */

import { useBilling, useTendingFor } from '@/features/billing/lib/useBilling';
import { useTranslation } from './useTranslation';

/** Whether the person is tending someone else's garden, and what a locked control says. */
export interface OwnerOnly {
  /** True once the server has said whose garden it is (the billing status is in). */
  known: boolean;
  /** True for a member tending someone else's garden (useTendingFor: the server's tendedOwnerOf). */
  tending: boolean;
  /** "Only the garden's owner can change this.", for a locked control's tooltip and tap; null in their own garden. */
  lockedReason: string | null;
}

/**
 * Whether the signed-in person is a member tending someone else's garden, and the words a locked control says.
 *
 * @returns known, tending, and lockedReason (null in their own garden).
 */
export function useOwnerOnly(): OwnerOnly {
  const { reactTranslate: t } = useTranslation();
  const { data } = useBilling();
  const tending = useTendingFor() !== null;
  return { known: data !== undefined, tending, lockedReason: tending ? t('garden.ownerOnly') : null };
}
